import {ActionIcon, Divider, Group, Title} from "@mantine/core";
import {
  ObjectMeterCheckAssocDataTable
} from "../../data-table/ObjectMeterCheckAssocDataTable/ObjectMeterCheckAssocDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  flowMeterType,
  getRequiredObjectId,
  MeterEntity,
  ObjectMeterCheckAssoc,
  objectMeterCheckAssocsSelector,
  useGetObjectMeterCheckAssocsByMeterQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntitySelectFormModal, EntitySelectFormValues} from "../../form/EntitySelectForm/EntitySelectForm";
import {useObjectMeterCheckAssocMutation} from "../../../hooks/useObjectMeterCheckAssocMutation";
import {useDisclosure} from "@mantine/hooks";

export type ObjectMeterCheckAssocDataTableCardProps = CardProps & {
  meter: MeterEntity
}

export const ObjectMeterCheckAssocDataTableCard = (props: ObjectMeterCheckAssocDataTableCardProps) => {
  const {
    meter,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])

  const [formOpen, formHandler] = useDisclosure(false)

  const selectObjectMeterCheckAssocs = useMemo(() => {
    return objectMeterCheckAssocsSelector();
  }, []);

  const {
    objectMeterCheckAssocs,
    isObjectMeterCheckAssocsLoading
  } = useGetObjectMeterCheckAssocsByMeterQuery(getRequiredObjectId(meter), {
    selectFromResult: (result) => ({
      objectMeterCheckAssocs: selectObjectMeterCheckAssocs(result),
      isObjectMeterCheckAssocsLoading: result.isLoading
    })
  });

  const {
    createObjectMeterCheckAssoc,
    deleteObjectMeterCheckAssoc,
    isCreating,
  } = useObjectMeterCheckAssocMutation()

  const onDeleteClick = useCallback((record: ObjectMeterCheckAssoc) => {
    deleteObjectMeterCheckAssoc(record.id, undefined)
  }, [deleteObjectMeterCheckAssoc])

  const onFormSubmit = useCallback((formValues: EntitySelectFormValues) => {
    const objectMeterCheckAssoc: Partial<ObjectMeterCheckAssoc> = {
      meterId: getRequiredObjectId(meter),
      objectId: formValues.object.id,
      objectType: formValues.object.type
    }
    createObjectMeterCheckAssoc(objectMeterCheckAssoc, () => formHandler.close())

  }, [createObjectMeterCheckAssoc, meter, formHandler])

  if (!isObjectMeterCheckAssocsLoading && objectMeterCheckAssocs.length > 0 || meter.flowMeterType === flowMeterType.check) {

    return (
      <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
        <Group position="apart" pb="sm">
          <Title order={4}>{t("common.entity.objectmetercheckassoc.plural", "Checking")}</Title>
          <Group spacing={4} position="left" noWrap>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={() => formHandler.open()}>
                <IconPlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
        </Group>

        <Divider pb="sm"/>

        <ObjectMeterCheckAssocDataTable
          withBorder
          records={objectMeterCheckAssocs}
          fetching={isObjectMeterCheckAssocsLoading}
          onDelete={onDeleteClick}
        />

        <EntitySelectFormModal
          title={t<string>("common.action.select", "Select")}
          opened={formOpen}
          onClose={formHandler.close}
          form={{
            onSubmit: onFormSubmit,
            submitting: isCreating,
            options: {
              options: ["SITE", "METER"],
              regionId: meter.regionId,
            }
          }}/>
      </Card>
    )
  }

  return <></>
}
